import {
    AfterViewInit, Component, EventEmitter, Input, Output,
    ViewChild,
    ChangeDetectorRef
} from '@angular/core';

import { WizardModalComponent } from "@app/shared/components";

import { PartNumber } from "@models/partNumber.model";

import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { PartNumberEquipmentFormComponent } from './forms/part-number-equipment-form/part-number-equipment-form.component';
import { PartNumberLinesFormComponent } from './forms/part-number-lines-form/part-number-lines-form.component';
import {
    PartNumberInsertionCyclesFormComponent
} from './forms/part-number-insertion-cycles-form/part-number-insertion-cycles-form.component';
import { PartNumberDetailsFormComponent } from './forms/part-number-details-form/part-number-details-form.component';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { PartNumberToolsFormComponent } from './forms/part-number-tools-form/part-number-tools-form.component';
import { PartnumberOverviewComponent } from '../partnumber-overview/partnumber-overview.component';
// tslint:disable-next-line:max-line-length
import {
    PartNumberEquivalenceFormComponent
} from "@app/roles/admin/pages/partnumbers/components/partnumber-modal/forms/part-number-equivalence-form/part-number-equivalence-form.component";
import { ToolService } from "@services/tool.service";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { Line } from "@models/line.model";
import { LineService } from "@services/line.service";
import { InsertionCycle } from "@models/insertion-cycle.model";
import { InsertionCyclesService } from "@services/insertion-cycles.service";
import { EquipmentService } from "@services/equipment.service";
import { Equipment } from "@models/equipment.model";
import { PartNumbersService } from "@services/part-numbers.service";
import {AuthenticationService} from "@app/authentication/services/authentication.service";

enum PartNumberSteps {
    DETAILS = 0,
    LINES = 1,
    INSERTION_CYCLES = 2,
    EQUIPMENTS = 3,
    EQUIVALENT_PNs = 4,
    TOOLS = 5,
    OVERVIEW = 6
}

@Component({
    selector: 'app-partnumber-modal',
    templateUrl: './partnumber-modal.component.html',
    styleUrls: ['./partnumber-modal.component.scss']
})
export class PartNumberModalComponent implements AfterViewInit {
    _logger = Log.create(PartNumberModalComponent.name);

    @ViewChild(WizardModalComponent) wizardModal: WizardModalComponent;
    @ViewChild(PartNumberDetailsFormComponent) partNumberDetailsForm: PartNumberDetailsFormComponent;
    @ViewChild(PartNumberLinesFormComponent) partNumberLinesForm: PartNumberLinesFormComponent;
    @ViewChild(PartNumberInsertionCyclesFormComponent) partNumberInsertionCyclesForm: PartNumberInsertionCyclesFormComponent;
    @ViewChild(PartNumberEquipmentFormComponent) partNumberEquipmentForm: PartNumberEquipmentFormComponent;
    @ViewChild(PartNumberEquivalenceFormComponent) partNumberEquivalenceForm: PartNumberEquivalenceFormComponent;
    @ViewChild(PartNumberToolsFormComponent) partNumberToolsForm: PartNumberToolsFormComponent;
    @ViewChild(PartnumberOverviewComponent) partNumberOverview: PartnumberOverviewComponent;

    @Input() isLoading;
    @Input() isReadOnly = false;
    @Output() onCreate: EventEmitter<any> = new EventEmitter();
    @Output() onUpdate: EventEmitter<any> = new EventEmitter();
    @Output() onDelete: EventEmitter<any> = new EventEmitter();
    @Output() onClose: EventEmitter<any> = new EventEmitter();

    public shouldRenderModifiers = false;
    public isPartNumberInUse = false;
    public canDeleteTooltipText = '';
    public duplicateMode = false;

    private partNumber: PartNumber;
    private previousPartNumberName: string;
    private basePartNumber: PartNumber;

    private selectedStepIndex = 0;
    public isWizardOpened = false;

    public lines: Line[] = [];
    public insertionCycles: InsertionCycle[] = [];
    public equipments: Equipment[] = [];
    public allConfiguredPartNumbers: PartNumber[] = [];
    public tools = [];
    public selectedTools = [];

    toolConfig = new TableConfig();
    selectedToolsConfig = new TableConfig();

    isLoadingTools = false;
    isLoadingSelectedTools = false;

    get isEditMode() {
        return !!this.partNumber && !!this.partNumber.id && !this.duplicateMode;
    }

    get isViewMode() {
        return this.partNumber && !this.hasPermission();
    }

    private get mode() {
        if (!this.hasPermission()) {
            return 'view'
        }
        if (this.duplicateMode) {
            return 'duplicate';
        }
        if (this.isEditMode) {
            return 'edit';
        }

        return 'create';
    }

    get partNumberDetailsValid() {
        return this.partNumberDetailsForm ? this.partNumberDetailsForm.isFormValid : false;
    }

    get partNumberLinesValid() {
        return this.partNumberLinesForm ? this.partNumberLinesForm.isFormValid : false;
    }

    get partNumberEquipmentsValid() {
        return this.partNumberEquipmentForm ? this.partNumberEquipmentForm.isFormValid : false;
    }

    constructor(private cdr: ChangeDetectorRef,
                private permissionService: PermissionService,
                private translate: TranslateService,
                private toolService: ToolService,
                private linesService: LineService,
                private insertionCyclesService: InsertionCyclesService,
                private equipmentService: EquipmentService,
                private authService: AuthenticationService,
                private partNumberService: PartNumbersService) {
        this.shouldRenderModifiers = this.permissionService.hasHasPermissionForFeature('MANAGE_PARTNUMBERS');
    }

    ngAfterViewInit(): void {
        this.cdr.markForCheck();
    }

    public onWizardStepChange(currentIndex) {
        let formData;

        if (!this.showSPI() && currentIndex >= PartNumberSteps.INSERTION_CYCLES) {
            currentIndex++;
        }

        switch (this.selectedStepIndex) {
            case PartNumberSteps.DETAILS:
                formData = this.partNumberDetailsForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                if (formData.basePartNumber) {
                    this.basePartNumber = formData.basePartNumber;
                    this.getBasePartNumber(formData.basePartNumber.id);
                } else {
                    this.basePartNumber = null;
                    this.initForms(currentIndex);
                }
                break;
            case PartNumberSteps.LINES:
                formData = this.partNumberLinesForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                this.initForms(currentIndex);
                break;
            case PartNumberSteps.INSERTION_CYCLES:
                formData = this.partNumberInsertionCyclesForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                this.initForms(currentIndex);
                break;
            case PartNumberSteps.EQUIPMENTS:
                formData = this.partNumberEquipmentForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                this.initForms(currentIndex);
                break;
            case PartNumberSteps.EQUIVALENT_PNs:
                formData = this.partNumberEquivalenceForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                this.initForms(currentIndex);
                break;
            case PartNumberSteps.TOOLS:
                formData = this.partNumberToolsForm.getFormDetails();
                Object.assign(this.partNumber, formData);
                this.initForms(currentIndex);
                break;
            case PartNumberSteps.OVERVIEW:
                this.initForms(currentIndex);
                break;
            default:
                break;
        }
        this.selectedStepIndex = currentIndex;
    }

    public showSPI() {
        if (this.partNumber) {
            return this.partNumber.lines.some(x => x.isSPI);
        }
    }

    public onWizardSubmit() {
        this.isEditMode ? this.updatePartNumber() : this.createPartNumber();
    }

    public onWizardDelete() {
        this.deletePartNumber();
    }

    public onWizardCancel() {
        this.close();
    }

    public onModalOpened() {
        this.previousPartNumberName = this.partNumber.name;
        this.isWizardOpened = true;
    }

    public close() {
        this.wizardModal.close();
        this.onClose.emit(this.partNumber);
        this.reset();
    }

    public open(partNumber?, duplicateMode = false) {
        this.getPartConfiguredNumbers();

        this.partNumber = _.cloneDeep(partNumber) || new PartNumber();
        this.duplicateMode = duplicateMode;

        this.wizardModal.open();
    }

    public get isDataLoading() {
        return this.isLoading;
    }

    public get isDataToolsLoading() {
        return this.isLoadingTools || this.isLoadingSelectedTools;
    }

    private initForms(currentIndex) {
        switch (currentIndex) {
            case PartNumberSteps.DETAILS:
                this.partNumberDetailsForm.init(this.partNumber, this.allConfiguredPartNumbers);
                break;
            case PartNumberSteps.LINES:
                this.getLines();
                this.partNumberLinesForm.init(this.partNumber);
                break;
            case PartNumberSteps.INSERTION_CYCLES:
                this.getInsertionCycles();
                this.partNumberInsertionCyclesForm.init(this.partNumber);
                break;
            case PartNumberSteps.EQUIPMENTS:
                this.getEquipments();
                this.partNumberEquipmentForm.init(this.partNumber);
                break;
            case PartNumberSteps.EQUIVALENT_PNs:
                this.loadSelectedEquivalentPartNumbers();
                this.partNumberEquivalenceForm.init(this.partNumber, this.allConfiguredPartNumbers);
                break;
            case PartNumberSteps.TOOLS:
                this.getTools();
                this.getSelectedTools();
                this.partNumberToolsForm.init(this.partNumber);
                break;
            case PartNumberSteps.OVERVIEW:
                this.partNumberOverview.init(this.partNumber);
                break;
            default:
                break;
        }
    }

    private createPartNumber(): void {
        this.onCreate.emit(this.partNumber);
        this.selectedStepIndex = 0;
    }

    private updatePartNumber(): void {
        this.onUpdate.emit({...this.partNumber, previousPartNumberName: this.previousPartNumberName});
        this.selectedStepIndex = 0;
    }

    private deletePartNumber(): void {
        this.onDelete.emit(this.partNumber);
    }

    private reset() {
        this.partNumber = new PartNumber();
        this.duplicateMode = false;
        this.selectedStepIndex = 0;

        if (this.partNumberDetailsForm) {
            this.partNumberDetailsForm.resetForm();
        }

        if (this.partNumberLinesForm) {
            this.partNumberLinesForm.resetForm();
        }

        if (this.partNumberInsertionCyclesForm) {
            this.partNumberInsertionCyclesForm.resetForm();
        }

        if (this.partNumberEquipmentForm) {
            this.partNumberEquipmentForm.resetForm();
        }

        if (this.partNumberEquivalenceForm) {
            this.partNumberEquivalenceForm.resetForm();
        }

        if (this.partNumberToolsForm) {
            this.partNumberToolsForm.resetForm();
        }

        this.wizardModal.reset();
    }

    public getTools(config = null) {
        this._logger.info('getTools:: Retrieving list of tools');

        const lineIds = this.partNumber.lines.map(l => l.id);

        this.isLoadingTools = true;
        this.tools = [];

        this.toolService.getToolsByMainLines(lineIds, config).subscribe({
            next: (tools: any) => {
                this._logger.info('getTools:: API returned data ', tools);
                this.tools = tools.tools;
                this.toolConfig.pager.count = tools.count;
                this.isLoadingTools = false;
            },
            error: () => {
                this.isLoadingTools = false;
            }
        });
    }

    private translateDeleteTooltipText() {
        this.isEditMode && this.shouldRenderModifiers && this.partNumber.inUse ?
            this.canDeleteTooltipText = this.translate.instant('admin.partNumbers.create-modal.button.deleteTooltipInUse') :
            this.canDeleteTooltipText = this.translate.instant('admin.partNumbers.create-modal.button.deleteTooltip')
    }

    private getSelectedTools() {
        this.selectedTools = [];
        this.isLoadingSelectedTools = true;

        const partNumberId = this.basePartNumber ? this.basePartNumber.id : this.partNumber.id;
        this.toolService.getToolsByPartNumber(partNumberId).subscribe(
            (data: any) => {
                data.tools.forEach(tool => {
                    if (!this.selectedTools.find(item => item.id === tool.id)) {
                        this.selectedTools.push(tool);
                    }
                });

                const pnLineIds = this.partNumber.lines.map(item => item.id);
                this.selectedTools = this.selectedTools.filter(tool => pnLineIds.includes(tool.mainLine.id));

                this.selectedToolsConfig.pager.count = this.selectedTools.length;
                this.isLoadingSelectedTools = false;
            },
            (error) => {
                this.isLoadingSelectedTools = false;
            }
        );
    }

    private getLines() {
        this._logger.info('getLines:: Retrieving list of lines');
        this.lines = [];
        this.isLoading = true;
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;

        this.linesService.getAllLines(tableConfig).subscribe((data: any) => {
                this._logger.info('getLines:: API returned', data);
                this.lines = data.items;
                this.lines.forEach(x => x.isSPI ? x.name = 'SPI - '.concat(x.name) : null)

                this.isLoading = false;
            },
            (error: any) => {
                this.isLoading = false;
                this._logger.error('getLines:: API returned error', error);
            })
    }

    private getInsertionCycles() {
        this._logger.info('getInsertionCycles:: Retrieving list of insertion cycles');
        this.insertionCycles = [];
        this.isLoading = true;
        this.insertionCyclesService.getInsertionCyclesByLinesIds(this.partNumber.lines.map(x => x.id))
            .subscribe((data: any) => {
                    this._logger.info('getInsertionCycles:: API Response', data);
                    this.insertionCycles = data.items;

                    this.loadSelectedInsertionCycles();

                    this.isLoading = false;
                },
                (error: any) => {
                    this._logger.error('getInsertionCycles:: API returned error', error);
                    this.isLoading = false;
                });
    }

    private loadSelectedInsertionCycles() {
        if (!this.partNumber) {
            return;
        }

        let {insertionCycles} = this.partNumber;
        const pnLineIds = this.partNumber.lines.map(item => item.id);

        this.partNumber.insertionCycles.forEach(ic => {
            const inLineIds = ic.assignedLines.map(item => item.id);
            if (!pnLineIds.some(item => inLineIds.includes(item))) {
                insertionCycles = insertionCycles.filter(item => item.id !== ic.id);
            }
        });

        this.partNumberInsertionCyclesForm.setForm({insertionCycles});
    }

    private getEquipments() {
        this._logger.info('getEquipment:: Retrieving list of equipments');
        this.isLoading = true;
        this.equipments = [];

        this.equipmentService.getAllEquipmentByLine(this.partNumber.lines).subscribe(
            (data: any) => {
                this._logger.info('getEquipment:: API returned', data);
                this.equipments = data.equipment;

                this.loadSelectedEquipment();

                this.isLoading = false;
            },
            (error: any) => {
                this._logger.error('getEquipment:: API returned error', error);
                this.isLoading = false;
            }
        )
    }

    private loadSelectedEquipment() {
        if (!this.partNumber) {
            return;
        }

        this.partNumber.equipment = this.partNumber.equipment.filter(
            item => this.partNumber.lines.map(l => l.id).includes(item.line.id)
        );

        const {equipment} = this.partNumber;
        this.partNumberEquipmentForm.setForm({equipment});
    }

    private getPartConfiguredNumbers() {
        this._logger.info('getPartNumbers:: Retrieving list of partNumbers');
        this.isLoading = true;

        this.allConfiguredPartNumbers = [];
        const tableConfig = new TableConfig();
        tableConfig.pager.pageSize = -1;
        tableConfig.filters.push({field: 'onlySummary', value: true});
        tableConfig.filters.push({field: 'status', value: 1});

        this.partNumberService.getAllPartNumbers(tableConfig).subscribe((data: any) => {
                this._logger.info('getPartNumbers:: API returned', data);
                this.allConfiguredPartNumbers = data.partNumbers;

                this.partNumberDetailsForm.init(this.partNumber, this.allConfiguredPartNumbers);
                this.isPartNumberInUse = this.partNumber.inUse;

                this.translateDeleteTooltipText();

                this.isLoading = false;
            },
            (error: any) => {
                this._logger.error('getPartNumbers:: API returned error', error);
                this.isLoading = false;
            })
    }

    private loadSelectedEquivalentPartNumbers() {
        if (!this.partNumber) {
            return;
        }

        if (this.duplicateMode) {
            const originalPartNumber = this.allConfiguredPartNumbers.find(item => item.id === this.partNumber.id);
            const isIncluded = this.partNumber.equivalentPartNumbers.some(item => item.id === originalPartNumber.id);

            if (originalPartNumber && !isIncluded) {
                this.partNumber.equivalentPartNumbers.unshift(originalPartNumber);
            }
        }

        const {equivalentPartNumbers} = this.partNumber;
        this.partNumberEquivalenceForm.setForm({equivalentPartNumbers});
    }

    private getBasePartNumber(id) {
        this._logger.info('getBasePartNumber:: Retrieving PartNumber with id' + id);
        this.isLoading = true;

        this.partNumberService.getPartNumberById(id).subscribe((data: any) => {
                this._logger.info('getBasePartNumber:: API returned', data);

                const configProperties = _.omit(data, ['id', 'name', 'description', 'sapGroup', 'sapFamily', 'valueStream',
                    'idMMDM', 'type', 'ecam', 'ecamVersion', 'bomStatus', 'status', 'nameWithSAP', 'inUse', 'status']);

                Object.assign(this.partNumber, configProperties);

                this.initForms(this.selectedStepIndex);

                this.isLoading = false;
            },
            (error: any) => {
                this._logger.error('getPartNumbers:: API returned error', error);
                this.isLoading = false;
            })
    }

    hasPermission() {
        return this.authService.hasPermission('MANAGE_PARTNUMBERS');
    }
}
